import { Eye, ShoppingCart } from "lucide-react";
import {
  CATALOGUE_NOTE,
  CATEGORIES,
  CONDITIONS,
  PRODUCTS,
  formatRM,
} from "@/data/products";
import ProductImage from "@/components/ProductImage";

const CONDITION_STYLES = {
  New: "bg-forest text-cream",
  "Pre-owned": "bg-bronze text-white",
  "Please enquire": "border border-charcoal/30 bg-white text-charcoal/80",
};

export function ConditionBadge({ condition }) {
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${CONDITION_STYLES[condition] || CONDITION_STYLES["Please enquire"]}`}
      data-testid={`condition-badge-${condition.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {condition}
    </span>
  );
}

function ProductCard({ product, onView, onAdd }) {
  return (
    <article
      className="flex flex-col overflow-hidden rounded-2xl border border-charcoal/10 bg-white shadow-sm transition-shadow hover:shadow-lg"
      data-testid={`product-card-${product.id}`}
    >
      <ProductImage product={product} className="aspect-[4/3] w-full border-b border-charcoal/5" />
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest">
            {product.category}
          </span>
          <ConditionBadge condition={product.condition} />
        </div>
        <h3 className="mt-3 font-display text-base font-semibold leading-snug text-charcoal">
          {product.name}
        </h3>
        <p className="mt-3 text-lg font-bold text-forest" data-testid={`product-price-${product.id}`}>
          {formatRM(product.price)}
        </p>
        <div className="mt-4 flex gap-2 pt-1">
          <button
            type="button"
            onClick={() => onView(product)}
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full border border-charcoal/20 px-3 py-2 text-xs font-semibold text-charcoal transition-colors hover:border-forest hover:text-forest"
            data-testid={`view-details-${product.id}`}
          >
            <Eye className="h-3.5 w-3.5" aria-hidden="true" />
            View Details
          </button>
          <button
            type="button"
            onClick={() => onAdd(product.id)}
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full bg-forest px-3 py-2 text-xs font-semibold text-cream transition-colors hover:bg-forest-mid"
            data-testid={`add-to-cart-${product.id}`}
          >
            <ShoppingCart className="h-3.5 w-3.5" aria-hidden="true" />
            Add to Cart
          </button>
        </div>
      </div>
    </article>
  );
}

export default function Products({
  category,
  condition,
  onCategoryChange,
  onConditionChange,
  onView,
  onAdd,
}) {
  const visible = PRODUCTS.filter((p) => p.visible);
  const filtered = visible.filter(
    (p) =>
      (category === "All Products" || p.category === category) &&
      (condition === "All Conditions" || p.condition === condition)
  );

  return (
    <section id="products" className="scroll-mt-20 bg-cream py-16 sm:py-20">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <h2 className="font-display text-3xl font-semibold text-charcoal sm:text-4xl">
          Product Catalogue
        </h2>
        <p className="mt-4 max-w-3xl rounded-xl border border-bronze/40 bg-bronze/10 p-4 text-sm leading-relaxed text-charcoal/80" data-testid="catalogue-note">
          {CATALOGUE_NOTE}
        </p>

        <div className="mt-8 flex flex-wrap gap-2" role="group" aria-label="Filter by category">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => onCategoryChange(c)}
              aria-pressed={category === c}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                category === c
                  ? "bg-forest text-cream"
                  : "border border-charcoal/20 bg-white text-charcoal/75 hover:border-forest hover:text-forest"
              }`}
              data-testid={`filter-category-${c.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="Filter by condition">
          {CONDITIONS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => onConditionChange(c)}
              aria-pressed={condition === c}
              className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                condition === c
                  ? "bg-bronze text-white"
                  : "border border-charcoal/20 bg-white text-charcoal/70 hover:border-bronze hover:text-bronze-dark"
              }`}
              data-testid={`filter-condition-${c.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {c}
            </button>
          ))}
        </div>

        <p className="mt-6 text-sm text-charcoal/60" data-testid="product-result-count">
          Showing {filtered.length} of {visible.length} products
        </p>

        <div className="mt-6 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3" data-testid="product-grid">
          {filtered.map((p) => (
            <ProductCard key={p.id} product={p} onView={onView} onAdd={onAdd} />
          ))}
        </div>
        {filtered.length === 0 && (
          <p className="mt-10 text-center text-sm text-charcoal/60" data-testid="no-products-message">
            No products match the selected filters.
          </p>
        )}
      </div>
    </section>
  );
}
